// @file store for object previews and handling object update to update previews in sandbox
import { unboundedWatch, vSet } from '@@/bits/vue'
import {
  createDebouncedHandlePageChange,
  getChangedIds,
  getSvgStringForShapes,
  isEditorIdle,
} from '@@/bits/whiteboard_preview_utils'
import { useWhiteboardScriptStore } from '@@/pinia/whiteboard_script'
import { useTldrawEditor } from '@@/vuecomposables/useTldrawEditor'
import { uniq } from 'es-toolkit'
import { defineStore } from 'pinia'
import type { HistoryEntry, TLRecord, TLShapeId } from 'tldraw'
import { computed, ref } from 'vue'

export const useWhiteboardObjectPreviewStore = defineStore('whiteboardObjectPreview', () => {
  const objectsPreviews = ref<Record<TLShapeId, string>>({})
  const objectsWithPreviewChanges = ref<TLShapeId[]>([])

  const objectIdsWithPreviews = computed(() => Object.keys(objectsPreviews.value) as TLShapeId[])

  async function updateObjectPreviews(objectIds: TLShapeId[]): Promise<void> {
    await Promise.all(
      objectIds.map(async (objectId) => {
        const preview = await getObjectPreview(objectId)
        if (preview != null) {
          vSet(objectsPreviews.value, objectId, preview)
        }
      }),
    )
  }

  async function getObjectPreview(objectId: TLShapeId): Promise<string | null> {
    const editor = useTldrawEditor().editor
    if (editor?.getShape(objectId) == null) {
      return null
    }
    return await getSvgStringForShapes([objectId])
  }

  const whiteboardScriptStore = useWhiteboardScriptStore()

  function handlePageChange(event: HistoryEntry<TLRecord>): void {
    const objectsWithChange = getChangedIds(event, 'shape')
    const filteredObjectsWithChange = Array.from(objectsWithChange).filter((objectId) => {
      // Currently, we only want to update the previews for the objects that have scripts
      return whiteboardScriptStore.checkIfScriptExists(objectId)
    }) as TLShapeId[]
    if (filteredObjectsWithChange.length === 0) return
    objectsWithPreviewChanges.value = uniq([...objectsWithPreviewChanges.value, ...filteredObjectsWithChange])
  }

  function conditionallyUpdateObjectPreviews(): void {
    if (objectsWithPreviewChanges.value.length === 0 || !isEditorIdle()) {
      return
    }
    void updateObjectPreviews(objectsWithPreviewChanges.value)
    objectsWithPreviewChanges.value = []
  }

  const debouncedHandlePageChange = createDebouncedHandlePageChange(handlePageChange)

  void unboundedWatch(objectsWithPreviewChanges, conditionallyUpdateObjectPreviews)

  return {
    objectsPreviews,
    objectIdsWithPreviews,
    updateObjectPreviews,
    getObjectPreview,
    handlePageChange,
    debouncedHandlePageChange,
    conditionallyUpdateObjectPreviews,
  }
})
